import { create } from 'zustand'
import { axiosInstance } from '@/lib/axios'
import type { IAffiliateDiscountCode, IAffiliateOrdersResponse, IAffiliateOrdersParams, IAffiliateOrder } from '@/app/auth/profile/types/affiliate'

interface IAffiliateStore {
  // Affiliate Status
  affiliateStatus: IAffiliateDiscountCode | null
  isLoading: boolean
  isInitialized: boolean
  fetchAffiliateStatus: () => Promise<void>
  clearAffiliateStatus: () => void
  
  // Affiliate Orders
  orders: IAffiliateOrder[]
  ordersPagination: IAffiliateOrdersResponse['data']['pagination'] | null
  ordersSummary: IAffiliateOrdersResponse['data']['summary'] | null
  isOrdersLoading: boolean
  isOrdersInitialized: boolean
  ordersError: string | null
  lastOrdersParams: IAffiliateOrdersParams | null
  fetchAffiliateOrders: (params?: IAffiliateOrdersParams) => Promise<void>
  clearAffiliateOrders: () => void
}

export const useAffiliateStore = create<IAffiliateStore>((set, get) => ({
  // Affiliate Status
  affiliateStatus: null,
  isLoading: false,
  isInitialized: false,

  fetchAffiliateStatus: async () => {
    const { isLoading, isInitialized } = get()
    
    // Prevent multiple simultaneous calls
    if (isLoading) return
    
    // Only fetch once unless explicitly cleared
    if (isInitialized) return

    set({ isLoading: true })
    
    try {
      const response = await axiosInstance.get("/api/affiliate/my-discount-code")
      if (response.status === 200 && response.data.success) {
        set({ 
          affiliateStatus: response.data.data,
          isLoading: false,
          isInitialized: true
        })
      } else {
        set({ 
          affiliateStatus: null,
          isLoading: false,
          isInitialized: true
        })
      }
    } catch (error: any) {
      // If 404 or no discount code found, it's normal - user hasn't registered yet
      if (error.response?.status !== 404) {
        console.error("Error fetching affiliate status:", error)
      }
      set({ 
        affiliateStatus: null,
        isLoading: false,
        isInitialized: true
      })
    }
  },

  clearAffiliateStatus: () => {
    set({ 
      affiliateStatus: null,
      isLoading: false,
      isInitialized: false
    })
  },

  // Affiliate Orders
  orders: [],
  ordersPagination: null,
  ordersSummary: null,
  isOrdersLoading: false,
  isOrdersInitialized: false,
  ordersError: null,
  lastOrdersParams: null,

  fetchAffiliateOrders: async (params?: IAffiliateOrdersParams) => {
    const { isOrdersLoading, lastOrdersParams } = get()
    
    // Prevent multiple simultaneous calls with same parameters
    if (isOrdersLoading && JSON.stringify(params) === JSON.stringify(lastOrdersParams)) {
      return
    }

    set({ isOrdersLoading: true, ordersError: null, lastOrdersParams: params || null })
    
    try {
      // Build query parameters
      const queryParams = new URLSearchParams()
      
      if (params?.page !== undefined) {
        queryParams.append("page", params.page.toString())
      }
      if (params?.per_page !== undefined) {
        queryParams.append("per_page", params.per_page.toString())
      }
      if (params?.status !== undefined) {
        queryParams.append("status", params.status.toString())
      }
      if (params?.date_from) {
        queryParams.append("date_from", params.date_from)
      }
      if (params?.date_to) {
        queryParams.append("date_to", params.date_to)
      }

      const queryString = queryParams.toString()
      const url = `/api/affiliate/my-orders${queryString ? `?${queryString}` : ""}`

      const response = await axiosInstance.get<IAffiliateOrdersResponse>(url)
      
      if (response.status === 200 && response.data.success) {
        set({
          orders: response.data.data.orders,
          ordersPagination: response.data.data.pagination,
          ordersSummary: response.data.data.summary,
          isOrdersLoading: false,
          isOrdersInitialized: true
        })
      } else {
        throw new Error("Failed to fetch affiliate orders")
      }
    } catch (error: any) {
      console.error("Error fetching affiliate orders:", error)
      const errorMessage = error.response?.data?.message || 
        error.message || 
        "Không thể tải danh sách đơn hàng. Vui lòng thử lại."
      
      set({
        ordersError: errorMessage,
        orders: [],
        ordersPagination: null,
        ordersSummary: null,
        isOrdersLoading: false,
        isOrdersInitialized: true
      })
    }
  },

  clearAffiliateOrders: () => {
    set({
      orders: [],
      ordersPagination: null,
      ordersSummary: null,
      isOrdersLoading: false,
      isOrdersInitialized: false,
      ordersError: null,
      lastOrdersParams: null
    })
  }
})) 